import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics, safeLogEvent } from '../firebase';

/** The shell title (public/index.html). */
const BASE_TITLE = 'C1 Staffing';

/**
 * Component to track page views in Firebase Analytics
 * Should be placed inside the Router but outside Routes
 */
const PageViewTracker: React.FC = () => {
  const location = useLocation();
  const firstView = useRef(true);

  useEffect(() => {
    /* ☠️ A page's title must not follow the visitor to the next page.
     *
     * Only two things in this app ever set one: the SEO function's injected
     * <title> on a job-posting deep link (functions/src/seo/jobPostingSeo.ts)
     * and JobPostingDetail's <Helmet> — the only title-setting Helmet in src/.
     * Nothing reset it, and GA4 reads `document.title` for every page_view, so
     * once someone opened a posting, the whole rest of their session — board,
     * sign-in, apply wizard, earnings, assignments — was reported under that
     * posting's name. "Oakland Arena - Legends Global" ranked as the platform's
     * #1 screen on a seventh of the traffic of postings that never appeared
     * (docs/claude/project_dashboard_analytics.md, 2026-09-21).
     *
     * The FIRST view is left alone: on a deep link that title is the
     * SEO-injected one, and Googlebot renders this app — overwriting it would
     * hand every indexed job page the title "C1 Staffing". JobPostingDetail's
     * Helmet re-applies its own title right after this effect, so the tab and
     * link previews are unchanged. */
    if (firstView.current) firstView.current = false;
    else document.title = BASE_TITLE;

    safeLogEvent(analytics, 'page_view', {
      page_path: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location.pathname]);

  return null;
};

export default PageViewTracker;
